import ICON__chevron_down from '@/assets/icons/font_awesome/chevron-down-solid.svg?react';
import { UI_PRIMITIVE__button } from '@/components/ui/UI_PRIMITIVE__button';
import { UI_PRIMITIVE__dropdown_menu } from '@/components/ui/dropdown/UI_PRIMITIVE__dropdown_menu';
import { UI_PRIMITIVE__dropdown_menu_content } from '@/components/ui/dropdown/UI_PRIMITIVE__dropdown_menu_content';
import { UI_PRIMITIVE__dropdown_menu_item } from '@/components/ui/dropdown/UI_PRIMITIVE__dropdown_menu_item';
import { UI_PRIMITIVE__dropdown_menu_trigger } from '@/components/ui/dropdown/UI_PRIMITIVE__dropdown_menu_trigger';
import { use_effect } from '@/hooks/_base/use_effect';
import { use_slice_generation, use_slice_ui, use_slice_workspace } from '@/hooks/store/use_slices';
import { PLACEHOLDER_bar_line } from '@/pages/_base/views/PLACEHOLDER_bar_line';
import { UTILS__graph_data } from '@/utils/UTILS__graph_data';
import { ENUM__workspace_entity_feature } from '@shared/dto/+workspace/workspace_entity/_enums/ENUM__workspace_entity_feature';
import { FIELD_PRIMITIVE__base_string } from '@shared/dto/generation/graph_invoke/_fields/_primitives/string/FIELD_PRIMITIVE__base_string';
import { DTO_GRAPH__instance } from '@shared/dto/generation/graph_invoke/_graph/DTO_GRAPH__instance';
import { useState } from 'react';

interface PROPS {
  field: FIELD_PRIMITIVE__base_string;
  on_value_changed?: (graph_new: DTO_GRAPH__instance, field_new: FIELD_PRIMITIVE__base_string) => void;
}

export function G_UI_FIELD_PRIMITIVE__string_dropdown({ field, on_value_changed = () => {} }: PROPS) {
  /*
   * STORE
   */

  const { generation_current } = use_slice_generation(['generation_current']);
  const { current_workspace_user } = use_slice_workspace(['current_workspace_user']);
  let { g_general__ui_disabled } = use_slice_ui(['g_general__ui_disabled']);

  /*
   * STATE
   */
  const [value, set_value] = useState(field.get_value());

  use_effect(() => set_value(field.get_value()), [field.value]);

  const trigger_id = `G_UI_FIELD_PRIMITIVE__string_dropdown_${field.meta.field_id}`;

  /*
   * EVENTS
   */
  async function on_item_selected(value_new: string) {
    if (value_new == field.get_value()) return;

    let field_new = field.clone({ value: value_new });

    let graph_new = generation_current.tool.graph.clone();

    graph_new.field__replace(field_new);

    on_value_changed(graph_new, field_new);

    await UTILS__graph_data.update(graph_new);
  }

  /*
   * METHODS
   */
  function is_disabled() {
    return g_general__ui_disabled || field.meta.ui__is_readonly;
  }

  function get_cursor() {
    return is_disabled() ? 'cursor-not-allowed' : 'cursor-pointer';
  }

  /*
   * RENDERS
   */
  function render_item(value_item: string) {
    return (
      <UI_PRIMITIVE__dropdown_menu_item
        key={`${trigger_id}_item_${value_item}`}
        {...{ value }}
        onClick={() => on_item_selected(value_item)}
        className={`
        w-full 
        text-left text-bar-foreground truncate
        cursor-pointer rounded-lg           
        hover:bg-accent   
        ${value == value_item ? 'bg-accent' : ''}
        `}
      >
        {value_item}
      </UI_PRIMITIVE__dropdown_menu_item>
    );
  }

  if (!current_workspace_user.is_feature_pertmitted(ENUM__workspace_entity_feature.ENTITY_GENERATION_UPDATE)) return <PLACEHOLDER_bar_line />;

  /*
   * HTML
   */
  return (
    <UI_PRIMITIVE__dropdown_menu>
      <UI_PRIMITIVE__dropdown_menu_trigger asChild>
        <UI_PRIMITIVE__button
          data-component={trigger_id}
          id={trigger_id}
          variant="outline-on-bar"
          disabled={is_disabled()}
          className={`relative flex flex-row justify-center gap-4 truncate w-full h-7 ${get_cursor()}`}
        >
          <span className="truncate text-sm">{value}</span>
          <ICON__chevron_down className="size-3 absolute right-4 top-1/2 -translate-y-1/2" />
        </UI_PRIMITIVE__button>
      </UI_PRIMITIVE__dropdown_menu_trigger>
      <UI_PRIMITIVE__dropdown_menu_content {...{ trigger_id }} className="rounded-lg">
        <div className="w-full max-h-52 overflow-y-auto">{field.get_value_range().map(render_item)}</div>
      </UI_PRIMITIVE__dropdown_menu_content>
    </UI_PRIMITIVE__dropdown_menu>
  );
}
